import React, { useState, useEffect, useRef } from 'react';
import { Activity, Database, Users, FileText, TrendingUp, Clock, MapPin, Zap, RefreshCw, MessageSquare, BarChart3, ClipboardList, Baby, User, AlertTriangle, Star } from 'lucide-react';
import { cn } from '../lib/utils';
import './DebugConsole.css';
import { Child } from '../types';

interface Observation {
  id: string;
  child_id: string;
  description: string;
  category?: string;
  subcategory?: string;
  severity?: number;
  duration?: number;
  time_of_day?: string;
  location?: string;
  context_completeness?: number;
  created_at: string;
}

interface DBMetrics {
  total_queries: number;
  avg_query_time: number;
  slow_queries: number;
  cache_hit_rate: number;
  queries_per_second: number;
}

interface Stats {
  total_families: number;
  total_children: number;
  total_observations: number;
  total_messages: number;
  average_completeness?: number;
}

interface Family {
  id: string;
  parent_name: string;
  children?: Array<{ name: string; gender?: string }>;
  children_count: number;
  message_count: number;
  session_count: number;
  observation_count?: number;
}

interface DebugConsoleProps {
  familyId: string | null;
  apiBase: string;
  isVisible: boolean;
  families: Family[];
  onFamilySelect: (familyId: string) => void;
  onRefreshFamilies: () => void;
  selectedFamilyId: string | null;
  wsClient?: any;
}

export const DebugConsole: React.FC<DebugConsoleProps> = ({ 
  familyId, 
  apiBase, 
  isVisible, 
  families, 
  onFamilySelect, 
  onRefreshFamilies,
  selectedFamilyId,
  wsClient
}) => {
  const [children, setChildren] = useState<Child[]>([]);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [dbMetrics, setDBMetrics] = useState<DBMetrics>({
    total_queries: 0,
    avg_query_time: 0,
    slow_queries: 0,
    cache_hit_rate: 0,
    queries_per_second: 0,
  });
  const [stats, setStats] = useState<Stats>({
    total_families: 0,
    total_children: 0,
    total_observations: 0,
    total_messages: 0,
  });
  const [aiUsage, setAiUsage] = useState({
    session_cost: 0,
    total_cost: 0,
    tokens_used: 0,
    cache_hits: 0,
    ai_calls: 0,
  });

  // Pulse state for tiles
  const [pulsingTiles, setPulsingTiles] = useState<Set<string>>(new Set());

  const triggerTilePulse = (tileId: string) => {
    setPulsingTiles(prev => new Set(prev).add(tileId));
    setTimeout(() => {
      setPulsingTiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(tileId);
        return newSet;
      });
    }, 1500);
  };

  useEffect(() => {
    if (familyId && isVisible) {
      loadDebugData();
    }
  }, [familyId, isVisible]);

  useEffect(() => {
    if (!wsClient || !isVisible) return;

    // Refresh debug data when various events occur
    const handleDataUpdate = () => {
      loadDebugData();
      // Pulse individual metric tiles when data updates via WebSocket
      triggerTilePulse('session-cost');
      triggerTilePulse('total-cost');
      triggerTilePulse('tokens-used');
      triggerTilePulse('cache-hits');
      triggerTilePulse('ai-calls');
      triggerTilePulse('observations');
      triggerTilePulse('completeness');
      triggerTilePulse('messages');
      triggerTilePulse('children');
      triggerTilePulse('total-queries');
      triggerTilePulse('queries-per-sec');
      triggerTilePulse('avg-time');
      triggerTilePulse('slow-queries');
    };

    // Listen for various WebSocket events
    wsClient.on('chat_message', handleDataUpdate);
    wsClient.on('observation_created', handleDataUpdate);
    wsClient.on('observation_updated', handleDataUpdate);
    wsClient.on('child_created', handleDataUpdate);
    wsClient.on('child_deleted', handleDataUpdate);
    wsClient.on('family_created', handleDataUpdate);
    wsClient.on('database_reseeded', handleDataUpdate);
    wsClient.on('stats', (data: any) => {
      console.log('📊 WebSocket stats received:', data);
      // Update DB metrics if available
      if (data.data && data.data.total_connections !== undefined) {
        // Could update connection stats here if needed
      }
    });

    return () => {
      wsClient.off('chat_message', handleDataUpdate);
      wsClient.off('observation_created', handleDataUpdate);
      wsClient.off('observation_updated', handleDataUpdate);
      wsClient.off('child_created', handleDataUpdate);
      wsClient.off('child_deleted', handleDataUpdate);
      wsClient.off('family_created', handleDataUpdate);
      wsClient.off('database_reseeded', handleDataUpdate);
      wsClient.off('stats', () => {});
    };
  }, [wsClient, isVisible]);

  const handleChildCreated = (newChild: Child) => {
    setChildren(prev => [...prev, newChild]);
    // Trigger pulse for children tile
    triggerTilePulse('children');
  };

  const loadDebugData = async () => {
    if (!familyId) return;

    try {
      // Load children
      const childrenRes = await fetch(`${apiBase}/families/${familyId}/children`);
      if (childrenRes.ok) {
        const childrenData = await childrenRes.json();
        setChildren(Array.isArray(childrenData) ? childrenData : []);
      }

      // Load observations/behaviors
      const obsRes = await fetch(`${apiBase}/families/${familyId}/behaviors`);
      if (obsRes.ok) {
        const obsData = await obsRes.json();
        setObservations(obsData.slice(0, 5)); // Show recent 5
      }

      // Load stats
      const statsRes = await fetch(`${apiBase}/chat/stats/${familyId}`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load DB metrics
      try {
        const metricsRes = await fetch(`${apiBase}/db-performance/metrics`);
        if (metricsRes.ok) {
          const response = await metricsRes.json();
          if (response.status === 'success' && response.summary) {
            setDBMetrics({
              total_queries: response.summary.total_queries || 0,
              avg_query_time: response.summary.average_time_ms || 0,
              slow_queries: response.summary.slow_queries || 0,
              cache_hit_rate: 0, // Not provided by this endpoint
              queries_per_second: response.summary.queries_per_second || 0,
            });
          }
        }
      } catch (e) {
        console.error('Failed to load DB metrics:', e);
      }

      // Load AI usage data from cache stats
      try {
        const cacheStatsRes = await fetch(`${apiBase}/debug/cache-stats`);
        if (cacheStatsRes.ok) {
          const cacheData = await cacheStatsRes.json();
          setAiUsage({
            session_cost: 0, // TODO: Track session cost separately
            total_cost: cacheData.total_cost_recorded || 0,
            tokens_used: cacheData.tokens_saved || 0,
            cache_hits: cacheData.total_hits || 0,
            ai_calls: cacheData.total_entries || 0,
          });
        }
      } catch (e) {
        console.error('Failed to load AI usage:', e);
      }
    } catch (error) {
      console.error('Failed to load debug data:', error);
    }
  };

  const getCompletenessColor = (completeness: number) => {
    if (completeness >= 0.7) return '#10b981';
    if (completeness >= 0.5) return '#f59e0b';
    return '#ef4444';
  };

  const getCompletenessIcon = (completeness: number) => {
    const color = getCompletenessColor(completeness);
    return <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />;
  };

  const getSeverityColor = (severity: number) => {
    if (severity >= 7) return '#ef4444';
    if (severity >= 4) return '#f59e0b';
    return '#10b981';
  };

  const getCategoryIcon = (category?: string) => {
    if (category === 'behavioral_event') return <AlertTriangle className="h-4 w-4" />;
    if (category === 'positive_behavior') return <Star className="h-4 w-4" />;
    return <ClipboardList className="h-4 w-4" />;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const avgCompleteness = observations.length > 0
    ? Math.round(observations.reduce((sum, o) => sum + (o.context_completeness || 0.1), 0) / observations.length * 100)
    : 0;

  if (!isVisible) return null;

  return (
    <div className="debug-console border-l bg-background">
      <div className="debug-header border-b bg-muted/40 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Debug Console</h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono text-xs bg-secondary px-2 py-1 rounded">
              {familyId?.substring(0, 8)}...
            </span>
          </div>
        </div>
      </div>

      <div className="debug-content overflow-y-auto p-4">
        <div className="grid grid-cols-2 gap-4">
          {/* LEFT COLUMN: Family, Children, Observations */}
          <div className="space-y-4">
            {/* Family Selector Section */}
            <div className="rounded-lg border bg-card">
          <div className="border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-xs">Family Selector</h3>
              </div>
              <button 
                onClick={onRefreshFamilies}
                className="text-xs px-2 py-1 rounded hover:bg-accent transition-colors"
                title="Refresh families"
              >
                <RefreshCw className="h-3 w-3" />
              </button>
            </div>
          </div>
          <div className="p-3 space-y-1.5">
            {families.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground py-4">No families found</div>
            ) : (
              families.map((family) => (
                <div
                  key={family.id}
                  onClick={() => onFamilySelect(family.id)}
                  className={`p-2 rounded-md border cursor-pointer transition-colors ${
                    selectedFamilyId === family.id 
                      ? 'bg-primary/10 border-primary' 
                      : 'bg-background hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-xs">{family.parent_name}</span>
                    <div className="flex items-center gap-1">
                      {family.children && family.children.slice(0, 2).map((child, idx) => (
                        <span key={idx} title={child.name}>
                          <User className="h-3 w-3 text-muted-foreground" />
                        </span>
                      ))}
                      {family.children && family.children.length > 2 && (
                        <span className="text-xs text-muted-foreground">+{family.children.length - 2}</span>
                      )}
                      {!family.children && family.children_count > 0 && (
                        <div className="flex items-center gap-1">
                          <Baby className="h-3 w-3" />
                          <span className="text-xs">{family.children_count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      <span>{family.message_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BarChart3 className="h-3 w-3" />
                      <span>{family.session_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ClipboardList className="h-3 w-3" />
                      <span>{family.observation_count || 0}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Family Members Section */}
        <div className="rounded-lg border bg-card">
          <div className="border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold text-sm">Family Members</h3>
            </div>
          </div>
          <div className="p-3 space-y-1.5">
            {children.length === 0 ? (
              <div className="text-center space-y-3">
                <div className="text-xs text-muted-foreground py-4">No children added yet</div>
                {familyId && (
                  <div className="text-xs text-muted-foreground">
                    [Child Creation Component]
                  </div>
                )}
              </div>
            ) : (
              <>
                {children.map((child) => (
                  <div key={child.id} className="flex items-center justify-between p-2 rounded-md border bg-background hover:bg-accent transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                        <User className="h-3 w-3 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-xs">{child.name}</div>
                        <div className="text-[10px] text-muted-foreground">{child.age ? `Age ${child.age}` : 'Age not set'}</div>
                      </div>
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">{child.id.substring(0, 6)}...</div>
                  </div>
                ))}
                {familyId && (
                  <div className="pt-2 border-t">
                    <div className="text-xs text-muted-foreground">
                      [Child Creation Component]
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Recent Observations Section */}
        <div className="rounded-lg border bg-card">
          <div className="border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold text-sm">Recent Observations</h3>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {observations.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground py-4">No observations logged yet</div>
            ) : (
              observations.map((obs) => {
                const childName = children.find(c => c.id === obs.child_id)?.name || 'Unknown';
                const completeness = obs.context_completeness || 0.1;
                const completePct = Math.round(completeness * 100);

                return (
                  <div key={obs.id} className="rounded-md border bg-background p-2.5 hover:bg-accent transition-colors space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                            {childName}
                          </span>
                          <span className="text-xs text-muted-foreground">{formatTimestamp(obs.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
                          style={{ 
                            backgroundColor: `${getCompletenessColor(completeness)}20`,
                            color: getCompletenessColor(completeness)
                          }}
                        >
                          {getCompletenessIcon(completeness)} {completePct}%
                        </span>
                        {obs.severity && obs.severity > 0 && (
                          <span
                            className="text-xs font-medium"
                            style={{ color: getSeverityColor(obs.severity) }}
                          >
                            {'●'.repeat(Math.min(obs.severity, 3))}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <div className="mt-0.5">{getCategoryIcon(obs.category)}</div>
                      <p className="text-xs leading-relaxed">{obs.description}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {obs.time_of_day && obs.time_of_day !== 'Not specified' && (
                        <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-xs">
                          <Clock className="h-3 w-3" /> {obs.time_of_day}
                        </span>
                      )}
                      {obs.location && obs.location !== 'Not specified' && (
                        <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-xs">
                          <MapPin className="h-3 w-3" /> {obs.location}
                        </span>
                      )}
                      {obs.duration && obs.duration > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-xs">
                          ⏱️ {obs.duration}m
                        </span>
                      )}
                      {obs.severity && obs.severity > 0 && (
                        <span 
                          className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium"
                          style={{
                            borderColor: getSeverityColor(obs.severity),
                            backgroundColor: `${getSeverityColor(obs.severity)}20`,
                            color: getSeverityColor(obs.severity)
                          }}
                        >
                          <Zap className="h-3 w-3" /> {obs.severity}/10
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
          </div>

          {/* RIGHT COLUMN: AI Usage, Statistics and Database Performance */}
          <div className="space-y-4">
            {/* AI Usage Section */}
            <div className="rounded-lg border bg-card">
              <div className="border-b bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Zap className="h-3 w-3 text-muted-foreground" />
                  <h3 className="font-semibold text-xs">AI Usage (This Session)</h3>
                </div>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('session-cost') ? 'tile-pulse' : ''}`}>
                  <div className="text-sm font-bold text-green-500">${aiUsage.session_cost.toFixed(2)}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Session Cost</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('total-cost') ? 'tile-pulse' : ''}`}>
                  <div className="text-sm font-bold text-red-500">${aiUsage.total_cost.toFixed(2)}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Total Cost</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('tokens-used') ? 'tile-pulse' : ''}`}>
                  <div className="text-sm font-bold text-purple-500">{aiUsage.tokens_used}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Tokens Used</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('cache-hits') ? 'tile-pulse' : ''}`}>
                  <div className="text-sm font-bold text-green-500">{aiUsage.cache_hits}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Cache Hits</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors col-span-2 ${pulsingTiles.has('ai-calls') ? 'tile-pulse' : ''}`}>
                  <div className="text-sm font-bold text-blue-500">{aiUsage.ai_calls}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">AI Calls</div>
                </div>
              </div>
            </div>

            {/* Statistics Section */}
            <div className="rounded-lg border bg-card">
              <div className="border-b bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3 w-3 text-muted-foreground" />
                  <h3 className="font-semibold text-xs">Statistics</h3>
                </div>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('observations') ? 'tile-pulse' : ''}`}>
                  <div className="text-lg font-bold text-primary">{observations.length}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Observations</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('completeness') ? 'tile-pulse' : ''}`}>
                  <div className="text-lg font-bold text-primary">{stats.average_completeness ? Math.round(stats.average_completeness * 100) : 0}%</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Completeness</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('messages') ? 'tile-pulse' : ''}`}>
                  <div className="text-lg font-bold text-primary">{stats.total_messages}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Messages</div>
                </div>
                <div className={`rounded-md border bg-background p-2 hover:bg-accent transition-colors ${pulsingTiles.has('children') ? 'tile-pulse' : ''}`}>
                  <div className="text-lg font-bold text-primary">{children.length}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Children</div>
                </div>
              </div>
            </div>

            {/* Database Performance Section */}
        <div className="rounded-lg border bg-card">
          <div className="border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-2">
              <Database className="h-3 w-3 text-muted-foreground" />
              <h3 className="font-semibold text-xs">Database Performance</h3>
            </div>
          </div>
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className={`rounded-md border bg-background p-2 ${pulsingTiles.has('total-queries') ? 'tile-pulse' : ''}`}>
                <div className="text-sm font-bold text-primary">{dbMetrics.total_queries}</div>
                <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Queries</div>
              </div>
              <div className={`rounded-md border bg-background p-2 ${pulsingTiles.has('queries-per-sec') ? 'tile-pulse' : ''}`}>
                <div className="text-sm font-bold text-primary">{dbMetrics.queries_per_second.toFixed(2)}/s</div>
                <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Queries/Sec</div>
              </div>
              <div className={`rounded-md border bg-background p-2 ${pulsingTiles.has('avg-time') ? 'tile-pulse' : ''}`}>
                <div className="text-sm font-bold text-primary">{dbMetrics.avg_query_time.toFixed(2)}ms</div>
                <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Avg Time</div>
              </div>
              <div 
                className={`rounded-md border bg-background p-2 cursor-pointer hover:bg-accent transition-colors ${pulsingTiles.has('slow-queries') ? 'tile-pulse' : ''}`}
                onClick={() => {/* TODO: Implement show slow queries */}}
                title="Click to show slow queries"
              >
                <div className="text-lg font-bold text-primary">{dbMetrics.slow_queries}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Slow Queries</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => {/* TODO: Implement clear & reseed */}}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
              >
                Clear & Reseed
              </button>
              <button 
                onClick={() => {/* TODO: Implement reset metrics */}}
                className="rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/80 transition-colors"
              >
                Reset Metrics
              </button>
              <button 
                onClick={() => {
                  triggerTilePulse('session-cost');
                  triggerTilePulse('total-cost');
                  triggerTilePulse('tokens-used');
                  triggerTilePulse('cache-hits');
                  triggerTilePulse('ai-calls');
                  triggerTilePulse('observations');
                  triggerTilePulse('completeness');
                  triggerTilePulse('messages');
                  triggerTilePulse('children');
                  triggerTilePulse('total-queries');
                  triggerTilePulse('queries-per-sec');
                  triggerTilePulse('avg-time');
                  triggerTilePulse('slow-queries');
                }}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors col-span-2"
              >
                Test Pulse (Debug)
              </button>
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};
